import { Map } from 'immutable';
import _ from 'lodash';

import BaseStore from './BaseStore';
import FavouritesStore from './FavouritesStore';
import { ActionTypes, FulfilmentType } from '../constants/AppConstants';
import config from '../config';

import Order from '../records/Order';

export class OrderStore extends BaseStore {
    deserialize (serialized) {
        let state = new Map({
            isLoading: false,
            discountCodeLoading: false,
            discountCodeMessage: null,
            isLoadingDeliveryOptions: false,
            postalFulfilmentStoredAddress: null,
            handoffDetails: null,
            handoffReferralReported: false,
        });
        if (serialized) {
            _.map(serialized, (value, key) => {
                if (key === 'discountCodeMessage' || key === 'postalFulfilmentStoredAddress') { state = state.set(key, null); } //investigate
                else if (value) { state = state.set(key, Order.fromJSON(value)); }
            });
        }
        return state;
    }

    setLoading({ isLoading }) {
        return this.setState(this.state.set('isLoading', isLoading));
    }

    setLoadingDeliveryOptions({ isLoadingDeliveryOptions }) {
        return this.setState(this.state.set('isLoadingDeliveryOptions', isLoadingDeliveryOptions));
    }

    getLoading() {
        return this.state.get('isLoading');
    }

    getLoadingDeliveryOptions() {
        return this.state.get('isLoadingDeliveryOptions');
    }

    getDiscountCodeLoading() {
        return this.state.get('discountCodeLoading');
    }

    getDiscountCodeMessage() {
        return this.state.get('discountCodeMessage');
    }

    getCurrent () {
        return this.state.get('current', new Order());
    }

    setCurrent (order) {
        this.setState(this.state.set('current', order));
    }

    handleOrderPaid ({ order }) {
        let newState = this.state
                        .set('current', new Order()) //Clear the current order
                        .set('paid', order); //Save it as a paid order

        this.setState(newState);
    }

    clearPaidOrder () {
        let newState = this.state.set('paid', new Order());
        this.setState(newState);
    }

    getPaid () {
        return this.state.get('paid');
    }

    setPostalFulfilmentStoredAddress (address) {
        return this.setState(this.state.set('postalFulfilmentStoredAddress', address));
    }

    getPostalFulfilmentStoredAddress () {
        return this.state.get('postalFulfilmentStoredAddress');
    }

    handleOrderCreated ({ order }) {
        this.setCurrent(order);
    }

    handleFulfilmentUpdated ({ path, value }) {
        let current = this.getCurrent();
        path.unshift('delivery');

        current = current.setIn(path, value);
        this.setCurrent(current.set('totalPrice', current.getTotalPriceWithDelivery()));
    }

    handleFulfilmentMethodUpdated ({ deliveryOption }) {
        let current = this.getCurrent();

        current = current.set('delivery', current.delivery.setOption(deliveryOption));
        this.setCurrent(current.set('totalPrice', current.getTotalPriceWithDelivery()));
    }

    handlePostalFulfilmentStoredAddress ({ address }) {
        this.setPostalFulfilmentStoredAddress(address);
    }

    handleOrderUpdated ({ order }) {
        let updatedOrder = order
            // Delivery details are deleted
            // when deleting a trip from an order - so we should
            // maintain what we had chosen before
            .set('delivery', this.getCurrent().get('delivery'))
            // API-BUG: Keep the fulfilment values, as this isnt in the api yet
            .set('availableDeliveryOptions', this.getCurrent().get('availableDeliveryOptions'));

        this.setCurrent(updatedOrder);
    }

    handleLeadPassengerUpdated({ trip, leadPassengerName }) {
        const order = this.getCurrent();
        const index = order.trips.indexOf(trip);
        const updatedOrder = order.setIn(['trips', index, 'selfPrintPassenger'], leadPassengerName);
        this.setCurrent(updatedOrder);
    }

    handlePaymentStarted () {
        this.setState(this.state.set('paid', null));
    }

    handleOrderDeleted () {
        let newState = this.state
                        .set('current', new Order())
                        .set('paid', new Order());
        this.setState(newState);
    }

    handleNewDeliveryOptions({ deliveryOptions }) {
        let updatedOrder = this.getCurrent().setAvailableDeliveryOptions(deliveryOptions);
        let updatedOption;

        // Check if any favourite fares (with delivery attached) exist in the order
        // and set the delivery to match that of the most recently added favourite fare
        if (updatedOrder.includesFavouriteFare(true)) {
            const mostRecentFare = updatedOrder.getFaresIncludedInFavourites(true).last();
            const orderTrip = updatedOrder.trips.filter(trip => trip.hasFare(mostRecentFare)).first();
            const favouriteDelivery = FavouritesStore.getFavourite(mostRecentFare, orderTrip).get('delivery');
            updatedOption = deliveryOptions.find(({ id }) => id === favouriteDelivery.deliveryMethodId);
        } else if (config.get('enableDefaultFulfilmentEticket')) {
            updatedOption = deliveryOptions.find(({ fulfilmentMethod }) => fulfilmentMethod === FulfilmentType.E_TICKET);
        } else if (config.get('enableDefaultFulfilmentSmartcard')) {
            updatedOption = deliveryOptions.find(({ fulfilmentMethod }) => fulfilmentMethod === FulfilmentType.SMARTCARD);
        }

        if (updatedOption) {
            updatedOrder = updatedOrder.setDeliveryOption(updatedOption);
        }

        this.setCurrent(updatedOrder);
    }

    handleTaxiBookingAvailability ({ onwardTaxiBookingAvailable }) {
        this.setState(this.state.set('onwardTaxiBookingAvailable', onwardTaxiBookingAvailable));
    }

    isTaxiBookingAvailable () {
        // QUICKFIX: The store defaults this key to an order for some reason
        if (_.isBoolean(this.state.get('onwardTaxiBookingAvailable'))) {
            return this.state.get('onwardTaxiBookingAvailable', false);
        }
        return false;
    }

    handleCheckDiscountCodeStarted () {
        this.setState(this.state.set('discountCodeLoading', true));
    }

    hideDiscountCodeMessage () {
        const newState = this.state.set('discountCodeMessage', '');
        this.setState(newState);
    }

    handleCheckDiscountCodeFinished ({ updatedOrder }) {
        this.handleOrderUpdated({ order: updatedOrder });

        const newState = this.state
            .set('discountCodeLoading', false)
            .set('discountCodeMessage', _.get(updatedOrder, 'discount.description'));
        this.setState(newState);

        // hiding the message after 2 sec
        setTimeout(() => {
            this.hideDiscountCodeMessage();
        }, 2000);

    }

    handleCheckDiscountCodeFailed ({ error }) {
        const newState = this.state
            .set('discountCodeLoading', false)
            .set('discountCodeMessage', _.get(error, 'body.errors') && _.get(error, 'body.errors')[0].message);
        this.setState(newState);

        // hiding the message after 2 sec
        setTimeout(() => {
            this.hideDiscountCodeMessage();
        }, 2000);
    }


    handleContactDetailsUpdated({email, telephoneNumber}) {
        const updated = this.getCurrent().merge({email, telephoneNumber});
        this.setCurrent(updated);
    }

    handleOrderHandoffUpdated({ handoffDetails }) {
        return this.setState(this.state.set('handoffDetails', handoffDetails));
    }

    getHandoffDetails() {
        return this.state.get('handoffDetails');
    }

    handleHandoffReferralReported({ referralReported }) {
        return this.setState(this.state.set('handoffReferralReported', referralReported));
    }

    getHandoffReferralReported() {
        return this.state.get('handoffReferralReported');
    }

    getActionHandlers () {
        return {
            [ ActionTypes.ORDER_LOADING ]: this.setLoading,
            [ ActionTypes.ORDER_DELIVERY_OPTIONS_LOADING ]: this.setLoadingDeliveryOptions,
            [ ActionTypes.ORDER_CREATED ]: this.handleOrderCreated,
            [ ActionTypes.ORDER_REFRESH_LOADED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_CREATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_DELETED ]: this.handleOrderUpdated,
            [ ActionTypes.SET_ORDER_CHARITY_OPTIONS_SUCCESS ]: this.handleOrderUpdated,
            [ ActionTypes.DELETE_ORDER_CHARITY_OPTIONS_SUCCESS ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_TRIP_LEAD_PASSENGER_UPDATED ]: this.handleLeadPassengerUpdated,
            [ ActionTypes.ORDER_PLUSBUS_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.ORDER_PAID ]: this.handleOrderPaid,
            [ ActionTypes.ORDER_DELIVERY_OPTIONS_LOADED ]: this.handleNewDeliveryOptions,
            [ ActionTypes.ORDER_UPDATED ]: this.handleOrderUpdated,
            [ ActionTypes.FULFILMENT_UPDATED ]: this.handleFulfilmentUpdated,
            [ ActionTypes.FULFILMENT_METHOD_UPDATED ]: this.handleFulfilmentMethodUpdated,
            [ ActionTypes.POSTAL_FULFILMENT_STORED_ADDRESS_UPDATED ]: this.handlePostalFulfilmentStoredAddress,
            [ ActionTypes.ADDRESS_CREATED ]: this.handlePostalFulfilmentStoredAddress,
            [ ActionTypes.PAYMENT_AUTH_STARTED ]: this.handlePaymentStarted,
            [ ActionTypes.PAY360_SESSION_CREATED ]: this.handlePaymentStarted,
            [ ActionTypes.BASKET_CLEARED ]: this.handleOrderDeleted,
            [ ActionTypes.ORDER_EXPIRED ]: this.handleOrderDeleted,
            [ ActionTypes.LOGGED_OUT ]: this.handleOrderDeleted,
            [ ActionTypes.LOGGED_IN ]: this.clearPaidOrder,

            [ ActionTypes.ORDER_TAXI_BOOKING_AVAILABILITY ]: this.handleTaxiBookingAvailability,

            [ ActionTypes.ORDER_LOAD_STARTED ]: this.handleOrderDeleted,
            [ ActionTypes.ORDER_LOADED ]: this.handleOrderCreated,
            [ ActionTypes.ORDER_LOAD_FAILED ]: this.handleOrderDeleted,
            [ ActionTypes.ORDER_CONTACT_DETAILS_UPDATED ]: this.handleContactDetailsUpdated,
            [ ActionTypes.ORDER_HANDOFF_UPDATED ]: this.handleOrderHandoffUpdated,
            [ ActionTypes.ORDER_HANDOFF_REFERRAL_REPORTED ]: this.handleHandoffReferralReported,

            [ ActionTypes.APPLY_DISCOUNT_CODE_STARTED ]: this.handleCheckDiscountCodeStarted,
            [ ActionTypes.APPLY_DISCOUNT_CODE_FINISHED ]: this.handleCheckDiscountCodeFinished,
            [ ActionTypes.APPLY_DISCOUNT_CODE_FAILED ]: this.handleCheckDiscountCodeFailed,

            [ ActionTypes.DELETE_DISCOUNT_CODE_STARTED ]: this.handleCheckDiscountCodeStarted,
            [ ActionTypes.DELETE_DISCOUNT_CODE_FINISHED ]: this.handleCheckDiscountCodeFinished,
            [ ActionTypes.DELETE_DISCOUNT_CODE_FAILED ]: this.handleCheckDiscountCodeFailed,

        };
    }
}

const storeInstance = new OrderStore({ storageKey: 'orders' });


export default storeInstance;



// WEBPACK FOOTER //
// ./src/stores/OrderStore.js